import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid2 as Grid, Alert, FormControlLabel, Switch, CircularProgress,
  Stack, Typography, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider, MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  usersApi, rolesApi, branchesApi,
  type CreateUserRequest, type UpdateUserRequest, type CreateUserBranchAccessRequest,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  userId?: string | null;
}

interface AccessRow extends CreateUserBranchAccessRequest { key: string }

export function UserForm({ open, onClose, userId }: UserFormProps) {
  const qc = useQueryClient();
  const isEdit = !!userId;

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [access, setAccess] = useState<AccessRow[]>([]);

  const branches = useQuery({ queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 60_000 });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => rolesApi.list(), staleTime: 60_000 });

  const detail = useQuery({
    queryKey: ["user", userId],
    queryFn: () => usersApi.get(userId!),
    enabled: !!userId && open,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && detail.data) {
      setEmail(detail.data.email);
      setFullName(detail.data.fullName);
      setPassword(""); setConfirmPwd("");
      setMfaEnabled(detail.data.mfaEnabled);
      setIsActive(detail.data.isActive);
      setAccess(detail.data.branchAccess.map(a => ({
        key: crypto.randomUUID(),
        branchId: a.branchId, roleId: a.roleId, warehouseIds: a.warehouseIds,
      })));
    } else if (!isEdit) {
      setEmail(""); setFullName(""); setPassword(""); setConfirmPwd("");
      setMfaEnabled(false); setIsActive(true); setAccess([]);
    }
  }, [open, isEdit, detail.data]);

  const addAccess = () => setAccess(a => [...a, { key: crypto.randomUUID(), branchId: "", roleId: "" }]);
  const updateAccess = (key: string, patch: Partial<AccessRow>) =>
    setAccess(a => a.map(r => r.key === key ? { ...r, ...patch } : r));
  const removeAccess = (key: string) => setAccess(a => a.filter(r => r.key !== key));

  const validate = (): string | null => {
    if (!email.trim()) return "Email is required";
    if (!fullName.trim()) return "Full name is required";
    if (!isEdit) {
      if (!password) return "Password is required";
      if (password.length < 8) return "Password must be at least 8 characters";
      if (password !== confirmPwd) return "Passwords do not match";
      if (!/[A-Z]/.test(password)) return "Password needs an uppercase letter";
      if (!/[a-z]/.test(password)) return "Password needs a lowercase letter";
      if (!/[0-9]/.test(password)) return "Password needs a digit";
    }
    if (access.length === 0) return "Add at least one branch access entry";
    for (const a of access) {
      if (!a.branchId || !a.roleId) return "Every access row needs both branch and role";
    }
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "User saved" },
    mutationFn: async () => {
      const cleanedAccess = access.map(a => ({
        branchId: a.branchId, roleId: a.roleId,
        warehouseIds: a.warehouseIds && a.warehouseIds.length ? a.warehouseIds : undefined,
      }));
      if (isEdit && userId) {
        const payload: UpdateUserRequest = {
          id: userId, fullName, isActive, mfaEnabled, branchAccess: cleanedAccess,
        };
        await usersApi.update(userId, payload);
        return userId;
      }
      const payload: CreateUserRequest = {
        email, fullName, password, mfaEnabled, branchAccess: cleanedAccess,
      };
      const r = await usersApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
  });

  const err = validate();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit user" : "New user"}</DialogTitle>
      <DialogContent dividers>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        {detail.isLoading && isEdit && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}

        {(!isEdit || detail.data) && (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField label="Email *" type="email" size="small" fullWidth
                  value={email} onChange={(e) => setEmail(e.target.value)} disabled={isEdit}
                  helperText={isEdit ? "Email cannot be changed" : "Used as the login ID"} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField label="Full name *" size="small" fullWidth
                  value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </Grid>
              {!isEdit && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField label="Password *" type="password" size="small" fullWidth
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      helperText="Min 8 chars; needs upper, lower, digit" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField label="Confirm password *" type="password" size="small" fullWidth
                      value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel label="MFA enabled"
                  control={<Switch checked={mfaEnabled} onChange={(_, v) => setMfaEnabled(v)} />} />
              </Grid>
              {isEdit && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel label="Active"
                    control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />} />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Branch access *</Typography>
              <Button startIcon={<AddIcon />} onClick={addAccess} size="small">Add access</Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 240 }}>Branch *</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Role *</TableCell>
                  <TableCell sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {access.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      <TextField select size="small" fullWidth value={row.branchId}
                        onChange={(e) => updateAccess(row.key, { branchId: e.target.value })}>
                        <MenuItem value="">— Pick branch —</MenuItem>
                        {branches.data?.map((b) => (
                          <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField select size="small" fullWidth value={row.roleId}
                        onChange={(e) => updateAccess(row.key, { roleId: e.target.value })}>
                        <MenuItem value="">— Pick role —</MenuItem>
                        {roles.data?.map((r) => (
                          <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeAccess(row.key)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {access.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                      No branch access. Add at least one row.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}

        {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => mutation.mutate()}
          disabled={!!err || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? "Save changes" : "Create user"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
